"use client";

import { Capacitor, registerPlugin } from "@capacitor/core";
import { createClientLogger } from "@/lib/logging/client";
import { isUnimplementedNativeCall } from "@/lib/native/bridge-availability";

const logger = createClientLogger("lib/native/payment-navigation");

type HiivePaymentNavigationPlugin = {
  begin(options: { ttlSeconds: number }): Promise<{ active: boolean }>;
  end(): Promise<{ active: boolean }>;
  isActive(): Promise<{ active: boolean }>;
  /**
   * Added after the first binaries shipped — every call MUST go through
   * `detectNativeUpiApps`, which reads UNIMPLEMENTED as "old build".
   */
  listUpiApps(): Promise<{ apps?: unknown; hasAnyUpiApp?: unknown }>;
};

const HiivePaymentNavigation = registerPlugin<HiivePaymentNavigationPlugin>(
  "HiivePaymentNavigation",
);

/**
 * How long the native shell will keep the payment window open.
 *
 * Matches the plugin's own ceiling on both platforms. The native side clamps
 * anyway, so this is the request, not the guarantee.
 */
const PAYMENT_LEASE_TTL_SECONDS = 15 * 60;

/**
 * A Razorpay payment cannot complete inside the app unless the native shell is
 * told to stop ejecting off-origin navigation to the system browser.
 *
 * Web is unaffected — there is no shell to ask, and the browser handles the
 * redirect itself.
 */
export function isNativePaymentShell(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Saved so the patch can be undone precisely. A module-level slot rather than a
 * closure because `end` can be called from a different component than `begin`
 * — the return leg often runs after a full document reload.
 */
let originalWindowOpen: typeof window.open | null = null;

/**
 * Force `window.open` to navigate the top-level document instead.
 *
 * On iOS this is not optional. `WebViewDelegationHandler.createWebViewWith`
 * calls `UIApplication.shared.open(url)` unconditionally — it never consults
 * `shouldOverrideLoad` and never consults `server.allowNavigation` — so a popup
 * is ejected to Safari no matter what the native plugin says. The only way to
 * keep it in the app is to make sure a popup is never requested.
 *
 * Razorpay should not open one at all once `redirect: true` is set, so treat
 * this as the belt to that braces. Where it does fire, a top-level navigation is
 * the same thing redirect mode would have done anyway.
 */
function patchWindowOpen() {
  if (typeof window === "undefined" || originalWindowOpen) return;
  originalWindowOpen = window.open;
  window.open = ((url?: string | URL) => {
    if (!url) return null;
    window.location.assign(typeof url === "string" ? url : url.toString());
    return null;
  }) as typeof window.open;
}

function restoreWindowOpen() {
  if (typeof window === "undefined" || !originalWindowOpen) return;
  window.open = originalWindowOpen;
  originalWindowOpen = null;
}

/**
 * Open the payment window: off-origin navigation stays inside the app until
 * `endNativePaymentNavigation` or the native lease expires.
 *
 * Returns whether the shell actually granted it. A `false` means an older build
 * without the plugin, where the payment will still eject — the caller should
 * keep the non-redirect flow rather than sending someone into a broken one.
 */
export async function beginNativePaymentNavigation(): Promise<boolean> {
  if (!isNativePaymentShell()) return false;

  try {
    await HiivePaymentNavigation.begin({
      ttlSeconds: PAYMENT_LEASE_TTL_SECONDS,
    });
    patchWindowOpen();
    return true;
  } catch (error) {
    // An app built before this plugin shipped. Native JS ships without a store
    // update but the SHELL does not, so this will be a real population for a
    // while — see project_native_js_ships_without_rebuild.
    logger.warn("[payments] native_payment_navigation_unavailable", {
      platform: Capacitor.getPlatform(),
      message: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
}

/**
 * Close the payment window.
 *
 * Safe to call unconditionally, including when nothing was ever opened: every
 * checkout screen calls it on mount to clear a lease stranded by the redirect
 * that destroyed the previous document.
 */
export async function endNativePaymentNavigation(): Promise<void> {
  restoreWindowOpen();
  if (!isNativePaymentShell()) return;

  try {
    await HiivePaymentNavigation.end();
  } catch {
    // Nothing to release on a shell without the plugin, and the native lease
    // expires on its own regardless.
  }
}

/**
 * What the native shell could tell us about the UPI apps on this device.
 *
 * - `detected`    — a new binary answered. `apps` are Razorpay shortcodes the OS
 *                   confirmed are installed (iOS: `canOpenURL` on each app's
 *                   scheme; Android: the packages that answer `upi://pay`).
 *                   `hasAnyUpiApp` is Android-only and gates the bare `upi://`
 *                   "Other UPI app" row; iOS always answers false.
 * - `unsupported` — not a native shell, or a binary built before the method
 *                   existed (UNIMPLEMENTED). The caller keeps the old path.
 * - `failed`      — the method exists but could not answer. Treated exactly
 *                   like `unsupported`: an unanswered probe must never read as
 *                   "no UPI apps", which would hide UPI from a payer who has it.
 */
export type NativeUpiAppDetection =
  | { status: "detected"; apps: string[]; hasAnyUpiApp: boolean }
  | { status: "unsupported" }
  | { status: "failed"; detail: string };

/**
 * Ask the shell which UPI apps are installed.
 *
 * Guarded because of the bridge contract: this JS reaches every installed
 * binary the moment it deploys, while `listUpiApps` only exists in builds made
 * after it was added. On those older builds Capacitor rejects with
 * UNIMPLEMENTED, and the checkout must then behave exactly as it did before —
 * one provider-managed "Pay by UPI app" row (`webview_intent`). See
 * project_native_bridge_contract_drift.
 */
export async function detectNativeUpiApps(): Promise<NativeUpiAppDetection> {
  if (!isNativePaymentShell()) return { status: "unsupported" };

  try {
    const result = await HiivePaymentNavigation.listUpiApps();
    const apps = Array.isArray(result?.apps)
      ? result.apps.filter(
          (entry): entry is string =>
            typeof entry === "string" && entry.length > 0,
        )
      : null;
    if (!apps) {
      return { status: "failed", detail: "malformed_native_answer" };
    }
    return {
      status: "detected",
      apps,
      hasAnyUpiApp: result.hasAnyUpiApp === true,
    };
  } catch (error) {
    if (isUnimplementedNativeCall(error)) {
      // Expected for a while after every deploy — an old binary, not an
      // outage — so it is info, not a warning.
      logger.info("[payments] native_upi_app_detection_unimplemented", {
        platform: Capacitor.getPlatform(),
      });
      return { status: "unsupported" };
    }
    const detail = error instanceof Error ? error.message : String(error);
    logger.warn("[payments] native_upi_app_detection_failed", {
      platform: Capacitor.getPlatform(),
      message: detail,
    });
    return { status: "failed", detail };
  }
}
